import React from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Info, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Inline Documentation System
 * 
 * Provides context-aware tooltips and documentation throughout the IDE.
 * Covers capabilities, effects, states, and IDE features.
 */

// Capability documentation database
export const CAPABILITY_DOCS = {
  // Time Domain
  'Clock.Now': {
    title: 'Clock.Now',
    domain: 'Time',
    description: 'Access current timestamp with timezone awareness.',
    useCases: ['Audit logging', 'Timestamp generation', 'Session tracking'],
    profiles: ['simulation', 'production'],
    example: 'const now = Clock.Now()'
  },
  'Temporal.Delay': {
    title: 'Temporal.Delay',
    domain: 'Time',
    description: 'Schedule delayed execution of effects.',
    useCases: ['Retry with backoff', 'Scheduled notifications', 'Timeout handling'],
    profiles: ['production'],
    example: 'Temporal.Delay(5000, () => retry())'
  },
  'Temporal.Schedule': {
    title: 'Temporal.Schedule',
    domain: 'Time',
    description: 'Schedule recurring or one-time tasks.',
    useCases: ['Cron jobs', 'Periodic cleanup', 'Scheduled reports'],
    profiles: ['production'],
    example: 'Temporal.Schedule("0 * * * *", task)'
  },
  
  // Storage Domain
  'Storage.Local': {
    title: 'Storage.Local',
    domain: 'Storage',
    description: 'Local key-value storage within machine context.',
    useCases: ['Caching', 'State persistence', 'Temporary data'],
    profiles: ['simulation', 'production'],
    example: 'Storage.Local.set("key", value)'
  },
  'Storage.KV': {
    title: 'Storage.KV',
    domain: 'Storage',
    description: 'Distributed key-value store with consistency guarantees.',
    useCases: ['Shared state', 'Feature flags', 'Session storage'],
    profiles: ['production'],
    example: 'await Storage.KV.get("user:123")'
  },
  'Storage.DO': {
    title: 'Storage.DO',
    domain: 'Storage',
    description: 'Durable Objects for strongly consistent state.',
    useCases: ['Counters', 'Rate limiting', 'Real-time collaboration'],
    profiles: ['production'],
    example: 'const counter = Storage.DO.get("counter")'
  },
  'Storage.SQL': {
    title: 'Storage.SQL',
    domain: 'Storage',
    description: 'SQL database access with transaction support.',
    useCases: ['Relational data', 'Complex queries', 'ACID transactions'],
    profiles: ['production'],
    example: 'await Storage.SQL.query("SELECT * FROM users")'
  },
  'Storage.Blob': {
    title: 'Storage.Blob',
    domain: 'Storage',
    description: 'Binary large object storage.',
    useCases: ['File uploads', 'Images', 'Documents'],
    profiles: ['production'],
    example: 'await Storage.Blob.put("file.pdf", data)'
  },
  
  // Network Domain
  'Net.HTTP': {
    title: 'Net.HTTP',
    domain: 'Network',
    description: 'Make HTTP/HTTPS requests to external services.',
    useCases: ['API calls', 'Webhooks', 'External integrations'],
    profiles: ['production'],
    example: 'await Net.HTTP.fetch("https://api.example.com")'
  },
  'Net.WS': {
    title: 'Net.WS',
    domain: 'Network',
    description: 'WebSocket connections for real-time communication.',
    useCases: ['Live updates', 'Chat', 'Streaming data'],
    profiles: ['production'],
    example: 'const ws = Net.WS.connect("wss://...")'
  },
  'Net.Queue': {
    title: 'Net.Queue',
    domain: 'Network',
    description: 'Message queue for async processing.',
    useCases: ['Background jobs', 'Event sourcing', 'Decoupling'],
    profiles: ['production'],
    example: 'await Net.Queue.publish("job", payload)'
  },
  'Net.Email': {
    title: 'Net.Email',
    domain: 'Network',
    description: 'Send transactional emails.',
    useCases: ['Notifications', 'Confirmations', 'Reports'],
    profiles: ['production'],
    example: 'await Net.Email.send({ to, subject, body })'
  },
  
  // Identity Domain
  'Auth.OAuth.Google': {
    title: 'Auth.OAuth.Google',
    domain: 'Identity',
    description: 'Google OAuth authentication.',
    useCases: ['Social login', 'Account linking', 'SSO'],
    profiles: ['production'],
    example: 'await Auth.OAuth.Google.initiate()'
  },
  'Auth.OAuth.GitHub': {
    title: 'Auth.OAuth.GitHub',
    domain: 'Identity',
    description: 'GitHub OAuth authentication.',
    useCases: ['Developer login', 'Repo access', 'SSO'],
    profiles: ['production'],
    example: 'await Auth.OAuth.GitHub.initiate()'
  },
  'Identity.Profile': {
    title: 'Identity.Profile',
    domain: 'Identity',
    description: 'Access authenticated user profile.',
    useCases: ['Personalization', 'Authorization', 'Audit'],
    profiles: ['simulation', 'production'],
    example: 'const user = Identity.Profile.get()'
  },
  'Identity.Session': {
    title: 'Identity.Session',
    domain: 'Identity',
    description: 'Session management and validation.',
    useCases: ['Login state', 'Session timeout', 'Multi-device'],
    profiles: ['simulation', 'production'],
    example: 'Identity.Session.validate(token)'
  },
  
  // Cryptography Domain
  'Crypto.Encrypt': {
    title: 'Crypto.Encrypt',
    domain: 'Cryptography',
    description: 'Encrypt sensitive data.',
    useCases: ['Data protection', 'Secure storage', 'PII handling'],
    profiles: ['production'],
    example: 'const encrypted = Crypto.Encrypt(data, key)'
  },
  'Crypto.Decrypt': {
    title: 'Crypto.Decrypt',
    domain: 'Cryptography',
    description: 'Decrypt encrypted data.',
    useCases: ['Data retrieval', 'Secure processing'],
    profiles: ['production'],
    example: 'const data = Crypto.Decrypt(encrypted, key)'
  },
  'Hash.SHA256': {
    title: 'Hash.SHA256',
    domain: 'Cryptography',
    description: 'Generate SHA-256 hash.',
    useCases: ['Integrity checks', 'Password hashing', 'Deduplication'],
    profiles: ['simulation', 'production'],
    example: 'const hash = Hash.SHA256(data)'
  },
  'Signature.Sign': {
    title: 'Signature.Sign',
    domain: 'Cryptography',
    description: 'Create digital signatures.',
    useCases: ['Document signing', 'Message authentication'],
    profiles: ['production'],
    example: 'const sig = Signature.Sign(data, privateKey)'
  },
  'Signature.Verify': {
    title: 'Signature.Verify',
    domain: 'Cryptography',
    description: 'Verify digital signatures.',
    useCases: ['Signature validation', 'Tamper detection'],
    profiles: ['production'],
    example: 'const valid = Signature.Verify(data, sig, publicKey)'
  },
  
  // Observability Domain
  'Log.Info': {
    title: 'Log.Info',
    domain: 'Observability',
    description: 'Log informational messages.',
    useCases: ['Debug output', 'Audit trail', 'Progress tracking'],
    profiles: ['simulation', 'production'],
    example: 'Log.Info("User logged in", { userId })'
  },
  'Log.Error': {
    title: 'Log.Error',
    domain: 'Observability',
    description: 'Log error messages.',
    useCases: ['Error tracking', 'Debugging', 'Alerts'],
    profiles: ['simulation', 'production'],
    example: 'Log.Error("Failed to process", { error })'
  },
  'Log.Trace': {
    title: 'Log.Trace',
    domain: 'Observability',
    description: 'Detailed trace logging.',
    useCases: ['Performance analysis', 'Deep debugging'],
    profiles: ['simulation', 'production'],
    example: 'Log.Trace("Step completed", { details })'
  },
  'Metric.Counter': {
    title: 'Metric.Counter',
    domain: 'Observability',
    description: 'Increment metric counters.',
    useCases: ['Request counting', 'Event tracking', 'KPIs'],
    profiles: ['production'],
    example: 'Metric.Counter.increment("requests")'
  },
  'Metric.Gauge': {
    title: 'Metric.Gauge',
    domain: 'Observability',
    description: 'Set gauge metrics.',
    useCases: ['Queue depth', 'Memory usage', 'Active users'],
    profiles: ['production'],
    example: 'Metric.Gauge.set("queue_size", 42)'
  },
  'Trace.Span': {
    title: 'Trace.Span',
    domain: 'Observability',
    description: 'Create distributed trace spans.',
    useCases: ['Performance monitoring', 'Distributed tracing'],
    profiles: ['production'],
    example: 'const span = Trace.Span.start("operation")'
  },
  
  // Orchestration Domain
  'Compile.TypeScript': {
    title: 'Compile.TypeScript',
    domain: 'Orchestration',
    description: 'Compile machine to TypeScript.',
    useCases: ['Code generation', 'Integration', 'Deployment'],
    profiles: ['simulation', 'production'],
    example: 'Compile.TypeScript(machine)'
  },
  'Compile.Codegen': {
    title: 'Compile.Codegen',
    domain: 'Orchestration',
    description: 'Generate code for target platforms.',
    useCases: ['Multi-platform deployment', 'SDK generation'],
    profiles: ['production'],
    example: 'Compile.Codegen(machine, "rust")'
  },
  'Orchestrator.Spawn': {
    title: 'Orchestrator.Spawn',
    domain: 'Orchestration',
    description: 'Spawn child machine instances.',
    useCases: ['Parallel workflows', 'Saga pattern', 'Process orchestration'],
    profiles: ['production'],
    example: 'Orchestrator.Spawn(childMachine, context)'
  },
  'Orchestrator.Join': {
    title: 'Orchestrator.Join',
    domain: 'Orchestration',
    description: 'Wait for child machines to complete.',
    useCases: ['Workflow completion', 'Result aggregation'],
    profiles: ['production'],
    example: 'await Orchestrator.Join(children)'
  }
};

// IDE feature documentation
export const IDE_DOCS = {
  'command-palette': {
    title: 'Command Palette',
    shortcut: '⌘K / Ctrl+K',
    description: 'Quick access to all IDE commands.',
    tips: ['Type to filter commands', 'Use arrow keys to navigate', 'Press Enter to execute']
  },
  'global-search': {
    title: 'Global Search',
    shortcut: '⌘F / Ctrl+F',
    description: 'Search across all machines, states, and effects.',
    tips: ['Searches machine names', 'Finds states and transitions', 'Jump directly to results']
  },
  'focus-mode': {
    title: 'Focus Mode',
    shortcut: 'F',
    description: 'Hide side panels for distraction-free editing.',
    tips: ['Press F to toggle', 'Great for graph editing', 'Panels remember their state']
  },
  'simulation': {
    title: 'Simulation Engine',
    description: 'Test your machine with live event dispatch.',
    tips: ['Dispatch events to test transitions', 'Use timeline to replay history', 'Fork to explore alternatives']
  }
};

/**
 * CapabilityTooltip - Hover documentation for capabilities
 */
export const CapabilityTooltip = ({ capability, children, side = 'right' }) => {
  const doc = CAPABILITY_DOCS[capability];
  
  if (!doc) {
    return children;
  }

  const domainColors = {
    'Time': 'bg-blue-500',
    'Storage': 'bg-green-500',
    'Network': 'bg-purple-500',
    'Identity': 'bg-amber-500',
    'Cryptography': 'bg-red-500',
    'Observability': 'bg-cyan-500',
    'Orchestration': 'bg-pink-500'
  };

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent 
        side={side} 
        className="w-80 bg-[#0a0e27] border-white/20 p-0"
      >
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-mono font-bold text-sm">{doc.title}</h4>
            <Badge className={cn("text-[10px]", domainColors[doc.domain])}>
              {doc.domain}
            </Badge>
          </div>
          <p className="text-sm text-gray-400">{doc.description}</p>
        </div>
        
        <div className="p-4 space-y-3">
          {/* Use Cases */}
          <div>
            <h5 className="text-[10px] uppercase text-gray-500 mb-1">Use Cases</h5>
            <div className="flex flex-wrap gap-1">
              {doc.useCases.map(useCase => (
                <Badge key={useCase} variant="outline" className="text-[10px]">
                  {useCase}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Supported Profiles */}
          <div>
            <h5 className="text-[10px] uppercase text-gray-500 mb-1">Available In</h5>
            <div className="flex gap-1">
              {doc.profiles.map(profile => (
                <Badge 
                  key={profile} 
                  variant="outline" 
                  className={cn(
                    "text-[10px]",
                    profile === 'simulation' ? 'border-green-500/50 text-green-400' : 'border-blue-500/50 text-blue-400'
                  )}
                >
                  <CheckCircle className="w-2.5 h-2.5 mr-1" />
                  {profile}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Example */}
          {doc.example && (
            <div>
              <h5 className="text-[10px] uppercase text-gray-500 mb-1">Example</h5>
              <code className="block text-[11px] bg-black/40 rounded px-2 py-1.5 text-blue-300 font-mono">
                {doc.example}
              </code>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

/**
 * HelpTooltip - Simple inline help icon with tooltip
 */
export const HelpTooltip = ({ content, className }) => (
  <HoverCard openDelay={200}>
    <HoverCardTrigger asChild>
      <button className={cn("p-1 hover:bg-white/10 rounded", className)}>
        <Info className="w-3.5 h-3.5 text-gray-500" />
      </button>
    </HoverCardTrigger>
    <HoverCardContent className="w-64 bg-[#0a0e27] border-white/20 text-sm">
      {content}
    </HoverCardContent>
  </HoverCard>
);

/**
 * FeatureTooltip - Documentation for IDE features
 */
export const FeatureTooltip = ({ feature, children }) => {
  const doc = IDE_DOCS[feature];
  
  if (!doc) return children;

  return (
    <HoverCard openDelay={400}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-72 bg-[#0a0e27] border-white/20 p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm">{doc.title}</h4>
          {doc.shortcut && (
            <kbd className="px-2 py-0.5 text-[10px] bg-white/10 rounded">
              {doc.shortcut}
            </kbd>
          )}
        </div>
        <p className="text-sm text-gray-400 mb-3">{doc.description}</p>
        {doc.tips && (
          <div className="space-y-1">
            {doc.tips.map((tip, i) => (
              <div key={i} className="flex items-start space-x-2 text-xs text-gray-500">
                <CheckCircle className="w-3 h-3 mt-0.5 text-green-500" />
                <span>{tip}</span>
              </div>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

/**
 * KeyboardShortcuts - Help overlay showing all shortcuts
 */
export const KeyboardShortcuts = ({ open, onOpenChange }) => {
  const shortcuts = [
    { category: 'Navigation', items: [
      { keys: ['⌘', 'K'], description: 'Command Palette' },
      { keys: ['⌘', 'F'], description: 'Global Search' },
      { keys: ['W'], description: 'Toggle Workspace Panel' },
      { keys: ['I'], description: 'Toggle Inspector Panel' },
      { keys: ['F'], description: 'Toggle Focus Mode' },
    ]},
    { category: 'Creation', items: [
      { keys: ['N'], description: 'New Machine (via palette)' },
      { keys: ['A', 'E'], description: 'Add Effect (via palette)' },
    ]},
    { category: 'Graph', items: [
      { keys: ['G'], description: 'Switch to Graph View' },
      { keys: ['+'], description: 'Zoom In' },
      { keys: ['-'], description: 'Zoom Out' },
      { keys: ['0'], description: 'Reset Zoom' },
    ]},
    { category: 'General', items: [
      { keys: ['?'], description: 'Show Shortcuts' },
      { keys: ['Esc'], description: 'Close Dialog / Deselect' },
    ]}
  ];

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => onOpenChange(false)}
    >
      <div 
        className="bg-[#0a0e27] border border-white/20 rounded-xl p-6 max-w-2xl w-full mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold">Keyboard Shortcuts</h2>
          <button 
            onClick={() => onOpenChange(false)}
            className="text-gray-500 hover:text-white"
          >
            <kbd className="px-2 py-1 text-xs bg-white/10 rounded">ESC</kbd>
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          {shortcuts.map(category => (
            <div key={category.category}>
              <h3 className="text-xs uppercase text-gray-500 mb-3">{category.category}</h3>
              <div className="space-y-2">
                {category.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">{item.description}</span>
                    <div className="flex items-center space-x-1">
                      {item.keys.map((key, ki) => (
                        <React.Fragment key={ki}>
                          <kbd className="px-2 py-1 text-xs bg-white/10 rounded min-w-[24px] text-center">
                            {key}
                          </kbd>
                          {ki < item.keys.length - 1 && (
                            <span className="text-gray-600">+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default {
  CapabilityTooltip,
  HelpTooltip,
  FeatureTooltip,
  KeyboardShortcuts,
  CAPABILITY_DOCS,
  IDE_DOCS
};
